import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaSearch,
  FaQuestionCircle,
  FaBook,
  FaVideo,
  FaComments,
  FaPhone,
  FaEnvelope,
  FaArrowRight,
  FaChevronDown,
  FaChevronUp,
  FaStar,
  FaClock,
  FaUserCheck,
  FaRobot,
  FaFileAlt,
  FaUsers,
  FaChartLine
} from 'react-icons/fa';
import HRChatbot from './HRChatbot'; // Import the chatbot component
import './HelpCenter.css';

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);

  // FAQ Data
  const faqCategories = [
    {
      id: 'getting-started',
      name: 'Getting Started',
      icon: <FaUserCheck />,
      color: '#3B82F6'
    },
    {
      id: 'account',
      name: 'Account & Billing',
      icon: <FaFileAlt />,
      color: '#8B5CF6'
    },
    {
      id: 'features',
      name: 'Features',
      icon: <FaRobot />,
      color: '#10B981'
    },
    {
      id: 'troubleshooting',
      name: 'Troubleshooting',
      icon: <FaUsers />,
      color: '#F59E0B'
    },
    {
      id: 'analytics',
      name: 'Analytics & Reports',
      icon: <FaChartLine />,
      color: '#EF4444'
    }
  ];

  const faqItems = [
    {
      id: 1,
      category: 'getting-started',
      question: 'How do I set up my HR Copilot account?',
      answer: 'To set up your HR Copilot account: 1. Sign up with your work email 2. Verify your email address 3. Complete company profile setup 4. Add team members 5. Configure your HR workflows 6. Import employee data (optional) 7. Start using the platform.',
      popular: true
    },
    {
      id: 2,
      category: 'getting-started',
      question: 'What are the system requirements?',
      answer: 'HR Copilot works on all modern browsers including Chrome, Firefox, Safari, and Edge. For mobile access, you can use our responsive web app or download our mobile apps from the App Store or Google Play Store.',
      popular: false
    },
    {
      id: 3,
      category: 'account',
      question: 'How do I update my billing information?',
      answer: 'To update billing information: Go to Settings > Billing > Payment Methods. Here you can add new payment methods, update existing ones, or change your subscription plan. All changes take effect immediately.',
      popular: true
    },
    {
      id: 4,
      category: 'account',
      question: 'Can I change my subscription plan?',
      answer: 'Yes, you can upgrade or downgrade your subscription at any time. Changes are prorated and will reflect in your next billing cycle. Go to Settings > Billing > Plans to make changes.',
      popular: false
    },
    {
      id: 5,
      category: 'features',
      question: 'How does the AI HR Assistant work?',
      answer: 'Our AI HR Assistant uses machine learning to automate routine HR tasks, provide insights, and answer employee queries. It learns from your company data to provide personalized responses and can handle common HR inquiries 24/7.',
      popular: true
    },
    {
      id: 6,
      category: 'features',
      question: 'Can I customize the employee onboarding process?',
      answer: 'Absolutely! You can fully customize the onboarding workflow: add custom forms, set up approval workflows, schedule training sessions, and automate welcome emails. Use our drag-and-drop workflow builder for easy customization.',
      popular: false
    },
    {
      id: 7,
      category: 'troubleshooting',
      question: 'What should I do if I forget my password?',
      answer: 'Click on "Forgot Password" on the login page. Enter your registered email address and we will send you a password reset link. The link expires in 24 hours for security purposes.',
      popular: false
    },
    {
      id: 8,
      category: 'troubleshooting',
      question: 'Why am I not receiving email notifications?',
      answer: 'First, check your spam folder. Then verify your email settings in your account profile. Ensure notifications are enabled in Settings > Notifications. If issues persist, contact our support team.',
      popular: true
    },
    {
      id: 9,
      category: 'analytics',
      question: 'How do I generate HR reports?',
      answer: 'Navigate to Analytics > Reports. Select the report type, date range, and filters you need. You can generate standard reports or create custom ones. Reports can be exported in PDF, Excel, or CSV formats.',
      popular: false
    },
    {
      id: 10,
      category: 'analytics',
      question: 'What analytics metrics are available?',
      answer: 'We provide comprehensive HR analytics including: employee turnover rates, hiring metrics, performance trends, engagement scores, training completion rates, diversity statistics, and cost-per-hire analysis.',
      popular: true
    }
  ];

  // Popular Articles
  const popularArticles = [
    {
      id: 1,
      title: 'Setting Up Employee Profiles',
      category: 'Getting Started',
      readTime: '5 min read',
      icon: <FaUserCheck />
    },
    {
      id: 2,
      title: 'Automating Leave Requests',
      category: 'Features',
      readTime: '3 min read',
      icon: <FaRobot />
    },
    {
      id: 3,
      title: 'Understanding HR Analytics',
      category: 'Analytics',
      readTime: '7 min read',
      icon: <FaChartLine />
    },
    {
      id: 4,
      title: 'Managing User Permissions',
      category: 'Account',
      readTime: '4 min read',
      icon: <FaUsers />
    }
  ];

  // Filter FAQs based on search and category
  const filteredFaqs = faqItems.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  return (
    <div className="help-center">
      {/* HR Chatbot Integration */}
      <HRChatbot />
      
      {/* Hero Section */}
      <section className="help-center__hero">
        <div className="help-center__container">
          <div className="help-center__hero-content">
            <h1 className="help-center__title">How can we help you?</h1>
            <p className="help-center__subtitle">
              Get answers to your questions about HR Copilot. Find tutorials, troubleshooting guides, and best practices.
            </p>
            
            {/* Search Bar */}
            <div className="help-center__search">
              <div className="help-center__search-container">
                <FaSearch className="help-center__search-icon" />
                <input
                  type="text"
                  placeholder="Search for answers, features, or issues..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="help-center__search-input"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Help Sections */}
      <section className="help-center__quick-help">
        <div className="help-center__container">
          <h2 className="help-center__section-title">Quick Help</h2>
          <div className="help-center__grid">
            <div className="help-center__card">
              <div className="help-center__card-icon help-center__card-icon--blue">
                <FaBook />
              </div>
              <h3>Knowledge Base</h3>
              <p>Comprehensive guides and documentation for all features</p>
              <Link to="/knowledge-base" className="help-center__card-link">
                Browse Articles <FaArrowRight />
              </Link>
            </div>

            <div className="help-center__card">
              <div className="help-center__card-icon help-center__card-icon--purple">
                <FaVideo />
              </div>
              <h3>Video Tutorials</h3>
              <p>Step-by-step video guides for common tasks</p>
              <Link to="/tutorials" className="help-center__card-link">
                Watch Videos <FaArrowRight />
              </Link>
            </div>

            <div className="help-center__card">
              <div className="help-center__card-icon help-center__card-icon--green">
                <FaComments />
              </div>
              <h3>Community Forum</h3>
              <p>Connect with other HR professionals and experts</p>
              <Link to="/community" className="help-center__card-link">
                Join Community <FaArrowRight />
              </Link>
            </div>

            <div className="help-center__card">
              <div className="help-center__card-icon help-center__card-icon--orange">
                <FaPhone />
              </div>
              <h3>Contact Support</h3>
              <p>Get personalized help from our support team</p>
              <Link to="/contact-support" className="help-center__card-link">
                Get Help <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="help-center__popular">
        <div className="help-center__container">
          <h2 className="help-center__section-title">Popular Articles</h2>
          <div className="help-center__articles-grid">
            {popularArticles.map(article => (
              <div key={article.id} className="help-center__article-card">
                <div className="help-center__article-icon">
                  {article.icon}
                </div>
                <div className="help-center__article-content">
                  <span className="help-center__article-category">
                    {article.category}
                  </span>
                  <h4 className="help-center__article-title">
                    {article.title}
                  </h4>
                  <div className="help-center__article-meta">
                    <FaClock />
                    <span>{article.readTime}</span>
                  </div>
                </div>
                <Link to={`/article/${article.id}`} className="help-center__article-link">
                  Read <FaArrowRight />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="help-center__faq">
        <div className="help-center__container">
          <div className="help-center__faq-header">
            <h2 className="help-center__section-title">Frequently Asked Questions</h2>
            
            {/* Category Filters */}
            <div className="help-center__categories">
              <button
                className={`help-center__category-btn ${activeCategory === 'all' ? 'help-center__category-btn--active' : ''}`}
                onClick={() => setActiveCategory('all')}
              >
                All Topics
              </button>
              {faqCategories.map(category => (
                <button
                  key={category.id}
                  className={`help-center__category-btn ${activeCategory === category.id ? 'help-center__category-btn--active' : ''}`}
                  onClick={() => setActiveCategory(category.id)}
                  style={{ '--category-color': category.color }}
                >
                  <span className="help-center__category-icon">
                    {category.icon}
                  </span>
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ List */}
          <div className="help-center__faq-list">
            {filteredFaqs.map(faq => (
              <div key={faq.id} className="help-center__faq-item">
                <div
                  className="help-center__faq-question"
                  onClick={() => toggleFaq(faq.id)}
                >
                  <div className="help-center__faq-question-content">
                    {faq.popular && (
                      <FaStar className="help-center__faq-popular" />
                    )}
                    <span>{faq.question}</span>
                  </div>
                  {expandedFaq === faq.id ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                {expandedFaq === faq.id && (
                  <div className="help-center__faq-answer">
                    <p>{faq.answer}</p>
                    <div className="help-center__faq-actions">
                      <button className="help-center__faq-action-btn">
                        <FaStar /> Helpful
                      </button>
                      <button className="help-center__faq-action-btn">
                        <FaEnvelope /> Share
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFaqs.length === 0 && (
            <div className="help-center__no-results">
              <FaQuestionCircle className="help-center__no-results-icon" />
              <h3>No results found</h3>
              <p>Try adjusting your search terms or browse different categories.</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="help-center__contact">
        <div className="help-center__container">
          <div className="help-center__contact-content">
            <div className="help-center__contact-text">
              <h2>Still need help?</h2>
              <p>Our support team is here to assist you with any questions or issues you might have.</p>
              <div className="help-center__contact-methods">
                <div className="help-center__contact-method">
                  <FaEnvelope className="help-center__contact-method-icon" />
                  <div>
                    <h4>Email Support</h4>
                    <p>support@hrcopilot.com</p>
                    <span>Typically replies within 2 hours</span>
                  </div>
                </div>
                <div className="help-center__contact-method">
                  <FaPhone className="help-center__contact-method-icon" />
                  <div>
                    <h4>Phone Support</h4>
                    <p>+1 (555) 123-4567</p>
                    <span>Mon-Fri, 9AM-6PM EST</span>
                  </div>
                </div>
                <div className="help-center__contact-method">
                  <FaComments className="help-center__contact-method-icon" />
                  <div>
                    <h4>Live Chat</h4>
                    <p>Available 24/7</p>
                    <span>Get instant help from our AI assistant</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="help-center__contact-actions">
              <button className="help-center__contact-btn help-center__contact-btn--primary">
                <FaEnvelope />
                Contact Support
              </button>
              <button className="help-center__contact-btn help-center__contact-btn--secondary">
                <FaComments />
                Start Live Chat
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}